// The IRC layout's attachments (#154): a cyan underlined link per file, `[image: IMG_4729.jpeg 1206×2347]`, that
// opens the shared lightbox (pictures, GIFs, videos) or downloads (other files). A private picture stays hidden
// until its viewer chooses to look, as in the other layouts. Chosen through MessageLayout.Attachments.
import { attachmentRef, isVisualAttachment, type Attachment } from "@bloom/shared";
import { downloadable } from "../../download.ts";
import { viewerPhoto } from "../../viewerPhoto.ts";
import { fileUrl, isImage, isVideo, variantUrl } from "../../upload.ts";
import { revealed, useInSet } from "../../viewerPrefs.ts";
import type { AttachmentsSlot } from "../MessageLayout.ts";
import { removeWithUndo } from "../undo.tsx";
import { attachmentLabel } from "./ircLogic.ts";

function Link({ messageId, a, onOpen, onRemove }: { messageId: string; a: Exclude<Attachment, { kind: "sticker" }>; onOpen: AttachmentsSlot["onOpen"]; onRemove?: () => void }) {
  const ref = attachmentRef(a);
  const seen = useInSet(revealed, ref);
  const label = attachmentLabel(a);
  const hidden = !!a.private && isVisualAttachment(a) && !seen;
  let link;
  if (hidden) {
    link = (
      <button type="button" className="irc-link" onClick={() => revealed.set(ref, true)}>
        [private {a.kind === "gif" ? "gif" : "picture"}: click to view]
      </button>
    );
  } else if (a.kind === "gif") {
    link = (
      <button type="button" className="irc-link" onClick={() => onOpen(a.url, a.url, { video: a.format === "video", width: a.width, height: a.height, download: downloadable(a) ?? undefined })}>
        {label}
      </button>
    );
  } else {
    const url = fileUrl(a.key);
    if (isImage(a.type)) {
      link = (
        <button type="button" className="irc-link" onClick={() => onOpen(variantUrl(a, "w1200.webp") ?? url, url, { download: downloadable(a) ?? undefined, photo: viewerPhoto(a) })}>
          {label}
        </button>
      );
    } else if (isVideo(a.type)) {
      link = (
        <button type="button" className="irc-link" onClick={() => onOpen(variantUrl(a, "h264.mp4") ?? url, url, { clip: true, download: downloadable(a) ?? undefined })}>
          {label}
        </button>
      );
    } else {
      link = <a className="irc-link" href={url} download={a.name}>{label}</a>;
    }
  }
  return (
    <span className="irc-att">
      {link}
      {onRemove && (
        <button type="button" className="irc-x" aria-label="Remove for everyone" title="Remove for everyone" onClick={() => removeWithUndo(`att:${messageId}|${ref}`, "Attachment removed", onRemove)}>
          [x]
        </button>
      )}
    </span>
  );
}

export function IrcAttachments({ messageId, attachments, onOpen, onRemove }: AttachmentsSlot) {
  return (
    <div className="irc-atts">
      {attachments.map((a) => {
        const ref = attachmentRef(a);
        if (a.kind === "sticker") return <span key={ref} className="irc-att">{attachmentLabel(a)}</span>;
        return <Link key={ref} messageId={messageId} a={a} onOpen={onOpen} onRemove={onRemove && (() => onRemove(ref))} />;
      })}
    </div>
  );
}
